import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { isDropinTask } from "@/features/tasks/isDropinTask";
import { isPhoneCallScheduledTask } from "@/features/tasks/isPhoneCallScheduledTask";
import {
  type TaskModalState,
  type TaskModalStateActions,
} from "@/features/tasks/taskModal/TaskModalReducer";
import {
  type Task,
  type TaskContext,
  type hfb,
  isTeamSelection,
  useConsent,
  useTeam,
} from "@repo/api";
import {
  FormField,
  InlineMessage,
  Input,
  ListView,
  ListViewItem,
  Option,
  Select,
  TextArea,
  briefcaseIcon,
  warehouseIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoworkerAndTeamSelectorListItem,
  ElementsContainer,
  getConsentValue,
  getParentTasksForContext,
  getSubTasksForContext,
  getTaskConfig,
  isConsentApproved,
  sortTaskTranslationKeys,
  translateHFB,
  translateTaskType,
  useBokaHfbs,
  useOrderedCustomerMeetingPoints,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import uniq from "lodash/uniq";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { getTaskTitle } from "../../utils";
import { DateForm } from "./DateForm";
import { DropinAppointmentForm } from "./DropinAppointmentForm";

export const TaskModalEdit = ({
  state,
  actions,
  task,
  hasCollision,
}: {
  state: TaskModalState;
  actions: TaskModalStateActions;
  task?: Task;
  hasCollision?: boolean;
}) => {
  const { calendar, task_configuration, language } = useSettings();
  const { pushModal } = useModals();
  const navigate = useNavigate();
  const { data: selectedTeamData } = useTeam(state.selectedTeam);
  const teamSelection = selectedTeamData
    ? {
        kind: "team" as const,
        team: selectedTeamData,
        assignedToTeam: true,
        members: (selectedTeamData.teamMembers ?? []).map((m) => ({
          ...m,
          selected: true,
        })),
      }
    : undefined;
  const { opportunity } = useActiveProject();
  const { data: cmp } = useOrderedCustomerMeetingPoints();
  const hfbs = useBokaHfbs();

  const selectedCMPname = cmp?.find(
    (cmpItem) => cmpItem[0] === state.cmpCode
  )?.[1];

  const taskContexts: TaskContext[] =
    state.context === "WORKSPACE" ? ["CALENDAR", "PROJECT"] : [state.context];

  const subTaskConfig = getTaskConfig(
    state.parentTaskType,
    state.subTaskType,
    task_configuration?.task_types
  )?.subTaskConfig;

  const subtypeDefaultTitleKey = translateTaskType(
    language,
    state.parentTaskType,
    state.subTaskType,
    task_configuration?.task_types,
    true
  );

  const sortedParentTaskTypes = sortTaskTranslationKeys(
    (key) =>
      translateTaskType(
        language,
        key,
        undefined,
        task_configuration?.task_types
      ),
    uniq(
      taskContexts.flatMap((context) =>
        getParentTasksForContext(
          context,
          calendar,
          task_configuration?.task_types,
          state.hfb
        )
      )
    )
  );

  const getSortedSubTaskTypes = (parentTaskType: string) =>
    parentTaskType
      ? sortTaskTranslationKeys(
          (key: string) =>
            translateTaskType(
              language,
              parentTaskType,
              key,
              task_configuration?.task_types
            ),
          uniq(
            taskContexts.flatMap((context) =>
              getSubTasksForContext(
                context,
                parentTaskType,
                calendar,
                task_configuration?.task_types,
                state.hfb
              )
            )
          )
        )
      : [];

  const isDropIn = isDropinTask(state.parentTaskType, state.subTaskType);
  const isPhoneCallScheduled = isPhoneCallScheduledTask(
    state.parentTaskType,
    state.subTaskType
  );

  const { data: consentData } = useConsent(
    isPhoneCallScheduled ? opportunity?.partyId : undefined
  );

  const followupConsentValue = getConsentValue(
    "FOLLOWUP_CONSENT",
    consentData?.customerConsents,
    opportunity?.hfb,
    opportunity?.id
  );
  const showFollowupConsentWarning =
    opportunity !== undefined && !isConsentApproved(followupConsentValue);

  const renderFields = {
    cmp: !isDropIn,
    openProject: !opportunity && task?.opportunityId !== undefined,
    hfb: !isDropIn,
    dropInForm: isDropIn,
    title: subTaskConfig?.hasTitle,
  };

  // set title when subtask changes and has no title configured
  useEffect(() => {
    if (!subTaskConfig?.hasTitle) {
      actions.setTitle(subtypeDefaultTitleKey);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.subTaskType]);

  return (
    <ElementsContainer>
      <ListView id="task-modal-list">
        <CoworkerAndTeamSelectorListItem
          title={t("label.assigned_to")}
          selectionMode={{
            coworkerSelectionMode: "MULTI",
            selectOnlyTeam: true,
          }}
          pushModal={pushModal}
          selected={
            teamSelection ?? state?.selectedCoworkers?.map((el) => el.emailId)
          }
          setSelected={(selected) => {
            if (isTeamSelection(selected)) {
              actions.setSelectedTeams(selected.team.teamId);
            } else {
              actions.setSelectedCoworkers(selected ?? undefined);
            }
          }}
          searchContext="CALENDAR"
        />
        {renderFields.cmp && (
          <ListViewItem
            control="navigational"
            title={t("label.cmp")}
            description={selectedCMPname}
            onClick={() => {
              pushModal({
                type: "SELECT_CMP",
                selectedCmp: state.cmpCode,
                onSelect: (cmp) => {
                  actions.setCmpCode(cmp);
                },
              });
            }}
            leadingIcon={warehouseIcon}
          />
        )}

        {renderFields.openProject && (
          <ListViewItem
            title={t("action.open_project")}
            onClick={() => {
              navigate(`/opportunity/${task?.opportunityId}`);
              trackAnalytics("open_project");
            }}
            description={task?.opportunityId?.toString() ?? ""}
            leadingIcon={briefcaseIcon}
            control="navigational"
          />
        )}
      </ListView>
      {renderFields.hfb && (
        <Select
          id="task-hfb-select"
          label={t("label.department")}
          onChange={(e) => actions.setHfb(e.target.value as hfb)}
          defaultValue={state.hfb}
        >
          {hfbs?.map(([key, _]) => (
            <Option
              key={`task-hfb-${key}`}
              value={key}
              name={translateHFB(key as hfb)}
            />
          ))}
        </Select>
      )}

      <ElementsContainer>
        <FormField
          shouldValidate={
            state.parentTaskType === undefined ||
            state.parentTaskType === t("action.choose_an_option")
          }
          valid={
            state.parentTaskType !== undefined &&
            state.parentTaskType !== t("action.choose_an_option")
          }
        >
          <Select
            id="tour-calendar-type-select"
            label={t("label.type")}
            value={state.parentTaskType}
            onChange={(e) => {
              actions.setParentTaskType(e.target.value);
            }}
            disabled={
              state.defaultTitle ||
              (state.mode === "EDIT" && isPhoneCallScheduled)
            }
          >
            {sortedParentTaskTypes.map((val) => {
              return <Option key={val.key} value={val.key} name={val.label} />;
            })}
          </Select>
        </FormField>
        <FormField
          shouldValidate={
            state.subTaskType === undefined ||
            state.subTaskType === t("action.choose_an_option")
          }
          valid={
            state.subTaskType !== undefined &&
            state.subTaskType !== t("action.choose_an_option")
          }
          validation={{
            id: "calendar-task-sub-type-select-error",
            type: "error",
            msg: t("prompt.please_select_subtype"),
          }}
        >
          <Select
            id="calendar-task-sub-type-select"
            label={t("label.subtype")}
            value={state.subTaskType ?? ""}
            disabled={
              state.defaultTitle ||
              (state.mode === "EDIT" && isPhoneCallScheduled)
            }
            onChange={(e) => {
              if (e.target.value) {
                actions.setSubTaskType({
                  subTaskType: e.target.value,
                });
                trackAnalytics("task_sub_type_select");
              }
            }}
            required
          >
            {state.parentTaskType !== undefined
              ? getSortedSubTaskTypes(state.parentTaskType).map((val) => (
                  <Option key={val.key} value={val.key} name={val.label} />
                ))
              : null}
          </Select>
        </FormField>

        {isPhoneCallScheduled && (
          <>
            <InlineMessage
              variant="informative"
              body={t(
                state.mode === "CREATE"
                  ? "label.task_created_customer_communication_info"
                  : "label.task_edited_customer_communication_info"
              )}
            />
            {showFollowupConsentWarning && (
              <InlineMessage
                variant="cautionary"
                body={t("prompt.consent_not_approved_task_communication")}
              />
            )}
          </>
        )}

        {renderFields.dropInForm && (
          <DropinAppointmentForm
            hfb={state.hfb}
            cmpCode={state.cmpCode}
            service={state.service}
            setCmpCode={actions.setCmpCode}
            setHfb={actions.setHfb}
            setService={actions.setService}
          />
        )}

        {renderFields.title && (
          <FormField
            shouldValidate={state.title === undefined}
            valid={state.title !== undefined && state.title.length > 0}
            validation={{
              id: "tour-calendar-task-title-error",
              type: "error",
              msg: t("prompt.please_enter_the_title"),
            }}
          >
            <Input
              id="tour-calendar-task-title"
              label={t("label.title")}
              type="text"
              value={getTaskTitle(state)}
              onChange={(e) => actions.setTitle(e.target.value)}
              disabled={state.defaultTitle}
              required
            />
          </FormField>
        )}
        <DateForm
          key={`${state.parentTaskType}${state.subTaskType}`}
          state={state}
          actions={actions}
          hasCollision={hasCollision}
          isDropInAppointment={isDropIn}
          isDateRangeOnlyTask={subTaskConfig?.isDateRangeOnlyTask}
        />
        <FormField
          fieldHelper={{
            msg: t("label.optional"),
          }}
        >
          <TextArea
            id="custom-task-comment"
            label={t("label.comment")}
            value={state.description}
            maxLength={2000}
            onChange={(e) => {
              actions.setDescription(e.target.value);
              trackAnalytics("custom-task-comment");
            }}
          />
        </FormField>
      </ElementsContainer>
    </ElementsContainer>
  );
};
